import * as React from "react";

import { cn } from "@/lib/utils";

function Table({ className, scrollLabels = { more: "More columns · scroll horizontally", left: "Scroll columns left", right: "Scroll columns right" }, ...props }: React.ComponentProps<"table"> & { scrollLabels?: { more: string; left: string; right: string } }) {
  const viewport = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = React.useState({ left: false, right: false });
  React.useEffect(() => {
    const node = viewport.current;
    if (!node) return;
    const update = () => setScroll({ left: node.scrollLeft > 1, right: node.scrollWidth - node.clientWidth - node.scrollLeft > 1 });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    node.addEventListener("scroll", update, { passive: true });
    update();
    return () => { observer.disconnect(); node.removeEventListener("scroll", update); };
  }, []);
  return (
    <div className="min-w-0 rounded-lg border bg-card" data-testid="table-panel">
      {(scroll.left || scroll.right) && <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <span>{scrollLabels.more}</span>
        <div className="flex shrink-0 gap-2">
          <button type="button" aria-label={scrollLabels.left} disabled={!scroll.left} className="rounded border bg-background px-3 py-1 font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-40" onClick={() => viewport.current?.scrollBy({ left: -360, behavior: "smooth" })}>←</button>
          <button type="button" aria-label={scrollLabels.right} disabled={!scroll.right} className="rounded border bg-background px-3 py-1 font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-40" onClick={() => viewport.current?.scrollBy({ left: 360, behavior: "smooth" })}>→</button>
        </div>
      </div>}
      <div ref={viewport} role="region" aria-label={props["aria-label"] ?? "Data table"} tabIndex={0} className="relative max-h-[36rem] w-full overflow-auto rounded-lg focus-visible:outline-2 focus-visible:outline-ring">
        <table className={cn("nb-table caption-bottom", className)} {...props} />
      </div>
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead className={cn("[&_tr]:border-b", className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "sticky top-0 z-10 whitespace-nowrap align-middle",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td className={cn("whitespace-nowrap align-top tabular-nums", className)} {...props} />
  );
}

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell };
